import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  Tag, Plus, Save, Trash2, X, Edit2, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle,
  MessageCircle, HelpCircle, Users, Share2
} from 'lucide-react';

interface AutoTagCondition {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'in';
  value: string | number | boolean | string[];
}

interface AutoTagRule {
  id: number;
  key: string;
  label: string;
  description?: string;
  explanation?: string;
  color: string;
  conditions: AutoTagCondition[];
  priority: number;
  isActive: number | boolean;
  whatsappTemplateKey?: string | null;
}

interface CMSEntry {
  key: string;
  label: string;
  category: string;
}

interface FieldOption {
  value: string;
  label: string;
  type: 'number' | 'text' | 'boolean' | 'tag';
}

const CUSTOMER_FIELDS: FieldOption[] = [
  { value: 'orderCount', label: '訂單數量', type: 'number' },
  { value: 'totalSpent', label: '累計消費（HKD）', type: 'number' },
  { value: 'daysSinceLastOrder', label: '距離最後下單天數', type: 'number' },
  { value: 'lastOrderAt', label: '最後下單時間（Unix）', type: 'number' },
  { value: 'source', label: '來源', type: 'text' },
  { value: 'isUnsubscribed', label: '已拒收', type: 'boolean' },
  { value: 'hasManualTag', label: '擁有手動標籤', type: 'tag' },
  { value: 'hasReferrer', label: '經推薦人介紹', type: 'boolean' },
];

const REFERRER_FIELDS: FieldOption[] = [
  { value: 'referralCount', label: '推薦單數', type: 'number' },
  { value: 'totalCommission', label: '累計佣金（分）', type: 'number' },
  { value: 'paidCommission', label: '已支付佣金（分）', type: 'number' },
  { value: 'unpaidCommission', label: '未支付佣金（分）', type: 'number' },
  { value: 'orderCount', label: '個人訂單數量', type: 'number' },
  { value: 'totalSpent', label: '個人累計消費（HKD）', type: 'number' },
  { value: 'totalPageViews', label: '累積分享連結瀏覽量', type: 'number' },
  { value: 'daysSinceJoined', label: '加入天數', type: 'number' },
  { value: 'status', label: '狀態', type: 'text' },
  { value: 'isUnsubscribed', label: '已拒收', type: 'boolean' },
  { value: 'hasManualTag', label: '擁有手動標籤', type: 'tag' },
];

const OPERATOR_OPTIONS: { value: AutoTagCondition['operator']; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: '包含' },
  { value: 'in', label: '在列表' },
];

const COLOR_OPTIONS = [
  { value: 'blue', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'purple', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'yellow', class: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'amber', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'green', class: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'red', class: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'gray', class: 'bg-gray-100 text-gray-700 border-gray-200' },
];

function formatConditionValue(cond: AutoTagCondition, fieldOptions: FieldOption[]): string {
  const field = fieldOptions.find((f) => f.value === cond.field);
  if ((field?.type === 'number' || cond.field === 'totalSpent' || cond.field === 'totalCommission' || cond.field === 'paidCommission' || cond.field === 'unpaidCommission') && typeof cond.value === 'number') {
    return cond.field === 'totalSpent' ? `HK$${(cond.value / 100).toFixed(0)}` : String(cond.value);
  }
  if (typeof cond.value === 'boolean') return cond.value ? '是' : '否';
  if (Array.isArray(cond.value)) return cond.value.join(', ');
  return String(cond.value);
}

function formatConditions(conditions: AutoTagCondition[], fieldOptions: FieldOption[]): string {
  return conditions.map((c) => {
    const field = fieldOptions.find((f) => f.value === c.field)?.label || c.field;
    const op = OPERATOR_OPTIONS.find((o) => o.value === c.operator)?.label || c.operator;
    return `${field} ${op} ${formatConditionValue(c, fieldOptions)}`;
  }).join(' 且 ');
}

function getColorClass(color: string): string {
  return COLOR_OPTIONS.find((c) => c.value === color)?.class || COLOR_OPTIONS[COLOR_OPTIONS.length - 1].class;
}

interface RulePanelProps {
  apiPrefix: string;
  fieldOptions: FieldOption[];
  title: string;
  description: string;
  whatsappTemplates: CMSEntry[];
  triggerNote: string;
}

function RulePanel({ apiPrefix, fieldOptions, title, description, whatsappTemplates, triggerNote }: RulePanelProps) {
  const [rules, setRules] = useState<AutoTagRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AutoTagRule | null>(null);

  const emptyForm: AutoTagRule = {
    id: 0,
    key: '',
    label: '',
    description: '',
    explanation: '',
    color: 'blue',
    conditions: [{ field: fieldOptions[0].value, operator: 'eq', value: fieldOptions[0].type === 'boolean' ? false : 0 }],
    priority: 0,
    isActive: 1,
    whatsappTemplateKey: '',
  };
  const [form, setForm] = useState<AutoTagRule>(emptyForm);

  const fetchRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(apiPrefix);
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setRules(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, [apiPrefix]);

  const showToast = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule: AutoTagRule) => {
    setEditing(rule);
    setForm({
      ...rule,
      conditions: rule.conditions.map((c) => ({ ...c })),
      isActive: rule.isActive ? 1 : 0,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleConditionChange = (index: number, patch: Partial<AutoTagCondition>) => {
    setForm((prev) => {
      const next = [...prev.conditions];
      next[index] = { ...next[index], ...patch };
      const fieldType = fieldOptions.find((f) => f.value === next[index].field)?.type;
      if (patch.field) {
        if (fieldType === 'boolean') next[index].value = false;
        else if (fieldType === 'tag') next[index].value = '';
        else next[index].value = 0;
      }
      return { ...prev, conditions: next };
    });
  };

  const addCondition = () => {
    const first = fieldOptions[0];
    setForm((prev) => ({ ...prev, conditions: [...prev.conditions, { field: first.value, operator: 'eq', value: first.type === 'boolean' ? false : 0 }] }));
  };

  const removeCondition = (index: number) => {
    setForm((prev) => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== index) }));
  };

  const normalizeValue = (cond: AutoTagCondition): string | number | boolean | string[] => {
    if (cond.field === 'totalSpent') {
      const n = Number(cond.value);
      return Number.isNaN(n) ? 0 : Math.round(n * 100);
    }
    if (cond.field === 'isUnsubscribed') return !!cond.value;
    if (cond.field === 'hasManualTag') return String(cond.value || '').trim();
    const fieldType = fieldOptions.find((f) => f.value === cond.field)?.type;
    if (fieldType === 'number') {
      const n = Number(cond.value);
      return Number.isNaN(n) ? 0 : n;
    }
    return String(cond.value || '');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.key.trim() || !form.label.trim()) {
      showToast('請填寫 Key 與標籤名稱', true);
      return;
    }
    if (!/^[a-z0-9_-]+$/.test(form.key.trim())) {
      showToast('Key 只可使用小寫英文、數字、底線或橫線', true);
      return;
    }
    if (form.conditions.length === 0) {
      showToast('請至少設定一個條件', true);
      return;
    }

    const payload = {
      key: form.key.trim(),
      label: form.label.trim(),
      description: form.description?.trim() || undefined,
      explanation: form.explanation?.trim() || undefined,
      color: form.color,
      conditions: form.conditions.map((c) => ({ ...c, value: normalizeValue(c) })),
      priority: Number(form.priority || 0),
      isActive: form.isActive ? true : false,
      whatsappTemplateKey: form.whatsappTemplateKey?.trim() || undefined,
    };

    try {
      const url = editing
        ? `${apiPrefix}/${encodeURIComponent(editing.key)}`
        : apiPrefix;
      const res = await apiFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast(editing ? '已更新規則' : '已建立規則');
      closeModal();
      fetchRules();
    } catch (err: any) {
      showToast(err.message || '儲存失敗', true);
    }
  };

  const handleDelete = async (rule: AutoTagRule) => {
    if (!confirm(`確定刪除「${rule.label}」(${rule.key})？`)) return;
    try {
      const res = await apiFetch(`${apiPrefix}/${encodeURIComponent(rule.key)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      showToast('已刪除');
      fetchRules();
    } catch (err: any) {
      showToast(err.message || '刪除失敗', true);
    }
  };

  const handleToggleActive = async (rule: AutoTagRule) => {
    try {
      const res = await apiFetch(`${apiPrefix}/${encodeURIComponent(rule.key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !rule.isActive }),
      });
      if (!res.ok) throw new Error('更新失敗');
      fetchRules();
    } catch (err: any) {
      showToast(err.message || '更新失敗', true);
    }
  };

  const displayValue = (cond: AutoTagCondition): string | number | boolean => {
    if ((cond.field === 'totalSpent' || cond.field === 'totalCommission' || cond.field === 'paidCommission' || cond.field === 'unpaidCommission') && typeof cond.value === 'number') {
      return cond.field === 'totalSpent' ? cond.value / 100 : cond.value;
    }
    return cond.value;
  };

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex justify-end gap-2">
        <button onClick={fetchRules} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
          <RefreshCw size={16} /> 重新整理
        </button>
        <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
          <Plus size={16} /> 新增規則
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          尚未建立自動標籤規則
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule) => (
            <div key={rule.key} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getColorClass(rule.color)}`}>
                      {rule.label}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{rule.key}</span>
                    {rule.isActive ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">啟用</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">停用</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    {formatConditions(rule.conditions, fieldOptions)}
                  </p>
                  {rule.explanation && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                      <HelpCircle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{rule.explanation}</span>
                    </div>
                  )}
                  {rule.description && <p className="text-xs text-gray-400 mt-1">{rule.description}</p>}
                  {rule.whatsappTemplateKey && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
                      <MessageCircle size={14} />
                      觸發 WhatsApp：{rule.whatsappTemplateKey}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleToggleActive(rule)} className={`${rule.isActive ? 'text-green-600' : 'text-gray-400'} hover:opacity-70`} title={rule.isActive ? '停用' : '啟用'}>
                    {rule.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button onClick={() => openEdit(rule)} className="text-blue-600 hover:text-blue-700" title="編輯">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(rule)} className="text-red-600 hover:text-red-700" title="刪除">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSave} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editing ? '編輯規則' : '新增規則'}</h2>
                <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={22} /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key *</label>
                    <input
                      disabled={!!editing}
                      value={form.key}
                      onChange={(e) => setForm({ ...form, key: e.target.value })}
                      placeholder="new-customer"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">小寫英文、數字、底線或橫線，建立後不可修改</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標籤名稱 *</label>
                    <input
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      placeholder="新客戶"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顏色</label>
                    <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                      {COLOR_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.value}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先級</label>
                    <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })} className="w-4 h-4 text-red-600 rounded" />
                      啟用
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明</label>
                  <input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="簡短描述這個標籤的用途" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">解釋（對客戶/團隊可見）</label>
                  <input value={form.explanation || ''} onChange={(e) => setForm({ ...form, explanation: e.target.value })} placeholder="例如：首次購買後自動標記為新客戶" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">條件（需全部符合）</label>
                  <div className="space-y-3">
                    {form.conditions.map((cond, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row gap-2 items-start md:items-center bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                        <select value={cond.field} onChange={(e) => handleConditionChange(idx, { field: e.target.value })} className="w-full md:w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                          {fieldOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <select value={cond.operator} onChange={(e) => handleConditionChange(idx, { operator: e.target.value as AutoTagCondition['operator'] })} className="w-full md:w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                          {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {fieldOptions.find((f) => f.value === cond.field)?.type === 'boolean' ? (
                          <select value={cond.value ? 'true' : 'false'} onChange={(e) => handleConditionChange(idx, { value: e.target.value === 'true' })} className="w-full md:flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm">
                            <option value="true">是</option>
                            <option value="false">否</option>
                          </select>
                        ) : (
                          <input
                            type={fieldOptions.find((f) => f.value === cond.field)?.type === 'number' ? 'number' : 'text'}
                            value={displayValue(cond)}
                            onChange={(e) => handleConditionChange(idx, { value: fieldOptions.find((f) => f.value === cond.field)?.type === 'number' ? Number(e.target.value) : e.target.value })}
                            className="w-full md:flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                          />
                        )}
                        <button type="button" onClick={() => removeCondition(idx)} className="text-red-600 hover:text-red-700"><X size={18} /></button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addCondition} className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium">+ 增加條件</button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">觸發 WhatsApp 模板（可選）</label>
                  <select value={form.whatsappTemplateKey || ''} onChange={(e) => setForm({ ...form, whatsappTemplateKey: e.target.value || undefined })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white">
                    <option value="">不觸發</option>
                    {whatsappTemplates.map((t) => <option key={t.key} value={t.key}>{t.label || t.key}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{triggerNote}</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2">
                  <Save size={16} /> 儲存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

interface ManualTag {
  tag: string;
  color: string;
  customerCount: number;
  referrerCount: number;
  totalCount: number;
  lastUsedAt: number | null;
}

function ManualTagsPanel() {
  const [tags, setTags] = useState<ManualTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/manual-tags');
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setTags(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleDelete = async (tag: string) => {
    if (!confirm(`確定刪除「${tag}」？\n這會同時移除所有客戶與推薦人身上的這個標籤，且無法復原。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/manual-tags/${encodeURIComponent(tag)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      setSuccess(`已刪除「${tag}」`);
      setTimeout(() => setSuccess(null), 3000);
      fetchTags();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex justify-end">
        <button onClick={fetchTags} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
          <RefreshCw size={16} /> 重新整理
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : tags.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          尚未有任何手動標籤
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 text-left font-medium">標籤名稱</th>
                <th className="px-4 py-3 text-center font-medium">客戶數</th>
                <th className="px-4 py-3 text-center font-medium">推薦人數</th>
                <th className="px-4 py-3 text-center font-medium">總數</th>
                <th className="px-4 py-3 text-left font-medium">最後使用</th>
                <th className="px-4 py-3 text-center font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {tags.map((t) => (
                <tr key={t.tag} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getColorClass(t.color)}`}>
                      {t.tag}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{t.customerCount}</td>
                  <td className="px-4 py-3 text-center">{t.referrerCount}</td>
                  <td className="px-4 py-3 text-center font-medium">{t.totalCount}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {t.lastUsedAt ? new Date(t.lastUsedAt * 1000).toLocaleString('zh-HK') : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDelete(t.tag)}
                      className="text-red-600 hover:text-red-700"
                      title="刪除標籤"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminAutoTags() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'customer' | 'referrer' | 'manual'>('customer');
  const [whatsappTemplates, setWhatsappTemplates] = useState<CMSEntry[]>([]);

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/public/admin/cms');
      if (!res.ok) return;
      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data || []) as CMSEntry[];
      setWhatsappTemplates(list.filter((e) => e.key.startsWith('whatsapp_')));
    } catch (e) {
      console.error('Failed to load WhatsApp templates', e);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchTemplates();
  }, [navigate]);

  return (
    <AdminLayout currentPage="auto-tags">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Tag className="w-6 h-6 text-red-600" />
          自動標籤規則
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          根據客戶或推薦人的行為條件自動打上標籤，並可綁定 WhatsApp 模板作為自動化觸發。
        </p>
      </div>

      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('customer')}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'customer'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Users size={16} /> 客戶標籤
          </button>
          <button
            onClick={() => setActiveTab('referrer')}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'referrer'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Share2 size={16} /> 推薦人標籤
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'manual'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Tag size={16} /> 手動添加標籤
          </button>
        </div>
      </div>

      {activeTab === 'customer' ? (
        <RulePanel
          apiPrefix="/api/public/admin/auto-tag-rules"
          fieldOptions={CUSTOMER_FIELDS}
          title="客戶自動標籤"
          description="根據客戶的訂單與互動行為自動分類。"
          whatsappTemplates={whatsappTemplates}
          triggerNote="當客戶符合條件，系統會在付款確認或訂單完成時自動發送此模板。"
        />
      ) : activeTab === 'referrer' ? (
        <RulePanel
          apiPrefix="/api/public/admin/referrer-tag-rules"
          fieldOptions={REFERRER_FIELDS}
          title="推薦人自動標籤"
          description="根據推薦人的業績與活躍度自動分類。"
          whatsappTemplates={whatsappTemplates}
          triggerNote="當推薦人符合條件，系統會在佣金入帳時自動發送此模板。"
        />
      ) : (
        <ManualTagsPanel />
      )}
    </AdminLayout>
  );
}
